/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DecisionMatrixPanel.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Card,
  Typography,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import CustomButtonV2 from "../../../../components/customComponentsV2/common/button/CustomButtonV2";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AISuggestionLoader from "../../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionLoader";
import TicketListDecisionMatrix from "./TicketsList";
import AiSuggestionDecisionMatrix from "./AiSuggestionDecisionMatrix";
import { HandleApiActions } from "../../../../redux/actions";
import smartMatchActions from "../../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { useDispatch } from "react-redux";
import { componentStyle } from "../../../../config/appTheme";

/**
 * Props:
 * - context: DecisionMatrixContext
 * - onExecuteWorkflow({ standard, ruleSet, workflow })
 * - onViewExecution(executionId)
 */
export default function DecisionMatrixPanel({
  context,
  onExecuteWorkflow,
  onViewExecution,
  onClose = {},
  onDecisionMatrixAction,
  handelGetSimilerTickets,
  setSimilarTickets,
  similarTickets,
  entityDetails,
  handelGetAiSuggestion,
  selectedTicket,
  openAiPanel,
  setOpenAiPanel,
  setCreateTicketData,
  disabledStandards,
}) {
  const standards = context?.standards || [];
  const [selectedStandardCode, setSelectedStandardCode] = useState(
    standards?.[0]?.standard_code || null,
  );
  const dispatch = useDispatch();
  const WORKFLOW_OUTCOMES = {
    CREATE_TICKET: "CREATE",
    DO_NOT_CREATE: "DO_NOT_CREATE",
  };

  const [executionState, setExecutionState] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);

  const [answers, setAnswers] = useState({});
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [hideRuleSets, setHideRuleSets] = useState(false);
  const [createdStandards, setCreatedStandards] = useState(new Set());
  const [uploads, setUploads] = React.useState({});

  const selectedStandard = useMemo(
    () => standards.find((s) => s.standard_code === selectedStandardCode),
    [standards, selectedStandardCode],
  );

  const [selectedRuleSetId, setSelectedRuleSetId] = useState(
    selectedStandard?.rule_sets?.[0]?.rule_set_id || null,
  );

  const selectedRuleSet = useMemo(
    () =>
      selectedStandard?.rule_sets.find(
        (rs) => rs.rule_set_id === selectedRuleSetId,
      ),
    [selectedStandard, selectedRuleSetId],
  );

  useEffect(() => {
    // Reset decision matrix when standard or rule set changes
    setExecutionState(null);
    setExecutionHistory([]);
    setAnswers({});
  }, [selectedStandardCode, selectedRuleSetId]);

  function statusColor(status) {
    switch (status) {
      case "PASS":
        return "success";
      case "FAIL":
        return "error";
      case "RUNNING":
        return "warning";
      default:
        return "default";
    }
  }

  function isValidExecutionStart(res) {
    return (
      res &&
      typeof res === "object" &&
      Object.keys(res).length > 0 &&
      typeof res.executionId === "string" &&
      typeof res.currentNodeType === "string"
    );
  }

  function isValidNodeResponse(res) {
    return (
      res &&
      typeof res === "object" &&
      typeof res.executionId === "string" &&
      (typeof res.type === "string" || // COMPLETE response
        typeof res.currentNodeType === "string") // defensive
    );
  }

  function getCurrentNodeId(executionState) {
    return (
      executionState?.currentNodeId || // START response
      executionState?.nodeId || // COMPLETE response
      null
    );
  }

  function getLabel(executionState) {
    return (
      executionState?.currentNodeLabel || // START response
      executionState?.label || // COMPLETE response
      null
    );
  }

  function aggregateStatus(ruleSets) {
    const workflows = ruleSets.flatMap((rs) => rs.workflows);
    if (workflows.some((w) => w.status === "FAIL")) return "FAIL";
    if (workflows.every((w) => w.status === "PASS")) return "PASS";
    if (workflows.some((w) => w.status === "RUNNING")) return "RUNNING";
    if (workflows.some((w) => w.status !== "NOT_EXECUTED")) return "PARTIAL";
    return "NOT_EXECUTED";
  }

  const [kgLoading, setKgLoading] = useState(false);

  const handleKgBulkRefresh = async () => {
    if (kgLoading) return { skipped: true };
    setKgLoading(true);

    try {
      let resp = await dispatch(
        HandleApiActions({
          https_method: "POST",
          ...smartMatchActions?.get_decision_matrix_ai_suggestions,
          params: {},
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      );
      return resp;
    } catch (e) {
      return { error: true };
    } finally {
      setKgLoading(false);
    }
  };

  useEffect(() => {
    if (!disabledStandards?.length) return;

    // If current standard got disabled → move to next available
    if (disabledStandards.includes(selectedStandardCode)) {
      const nextAvailable = standards.find(
        (std) =>
          std.rule_sets.length > 0 &&
          !disabledStandards.includes(std.standard_code),
      );

      if (nextAvailable) {
        setSelectedStandardCode(nextAvailable.standard_code);
        setSelectedRuleSetId(nextAvailable?.rule_sets?.[0]?.rule_set_id);
        resetDecisionMatrixState();
      } else if (openAiPanel) {
        resetDecisionMatrixState();
      }
    }
  }, [disabledStandards, standards, selectedStandardCode]);

  const resetDecisionMatrixState = () => {
    setExecutionState(null);
    setExecutionHistory([]);
    setAnswers({});
    setShowAiPanel(false);
    setHideRuleSets(false);
    setOpenAiPanel(false);
    setSimilarTickets([]);
    setUploads({});
  };

  useEffect(() => {
    // Whenever user selects another legal requirement
    // Reset AI & rule visibility cleanly

    setShowAiPanel(false);
    setHideRuleSets(false);
    setOpenAiPanel(false);
    setSimilarTickets([]);
  }, [selectedStandardCode]);

  function handelCloseDialog() {
    resetDecisionMatrixState();
    onClose();
  }

  const handleCreateTicket = async (payload) => {
    const res = await onDecisionMatrixAction?.(payload);
    if (res && !res?.error) {
      handelCloseDialog();
    }
    return res;
  };

  useEffect(() => {
    if (!standards?.length) return;

    const validStandards = standards?.filter(
      (std) => std.rule_sets?.length > 0,
    );

    if (!validStandards?.length) return;

    const allCompleted = validStandards?.every((std) =>
      disabledStandards?.includes(std.standard_code),
    );

    if (allCompleted) {
      handelCloseDialog();
    }
  }, [disabledStandards, standards]);
  return (
    <Box
      sx={{
        p: 2,
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        // overflow: "hidden",
      }}
    >
      <Box sx={{ mb: 2, display: "flex", justifyContent: "end" }}>
        {showAiPanel && (
          <CustomButtonV2
            type="other"
            size="small"
            btnName={kgLoading ? "Refreshing…" : "Refresh AI Suggestions"}
            handleOnClick={handleKgBulkRefresh}
            disabled={kgLoading}
          />
        )}
      </Box>
      {/* Decision Matrix content */}
      <Grid
        container
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* LEFT – LEGAL STANDARDS */}
        <Grid item xs={3} sx={{ height: "100%", minHeight: 0 }}>
          <Box
            sx={{
              border: "1px solid #D0D0D0",
              borderRadius: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: "1px solid #D0D0D0",
                fontWeight: 600,
                backgroundColor: "#FAFAFA",
              }}
            >
              Legal Standards
            </Box>

            <List dense sx={{ p: 1 }}>
              {standards
                ?.filter((std) => std.rule_sets.length > 0)
                ?.map((std) => {
                  const agg = aggregateStatus(std.rule_sets);
                  const selected = std.standard_code === selectedStandardCode;

                  return (
                    <ListItemButton
                      key={std.standard_code}
                      disabled={disabledStandards.includes(std.standard_code)}
                      selected={selected}
                      onClick={() => {
                        if (std.standard_code === selectedStandardCode) return;
                        setSelectedStandardCode(std.standard_code);
                        setSelectedRuleSetId(std.rule_sets?.[0]?.rule_set_id);
                      }}
                      sx={{
                        mb: 1,
                        borderRadius: 0,
                        border: selected
                          ? "2px solid #26890D"
                          : "1px solid #D0D0D0",
                        backgroundColor: selected ? "#F3FAF4" : "#FFFFFF",
                        "&.Mui-selected": {
                          backgroundColor: "#F3FAF4",
                        },
                      }}
                    >
                      <ListItemText
                        primary={std.standard_name}
                        secondary={`${std?.rule_sets?.filter((rs) => rs?.workflows?.length > 0)?.length} rule sets`}
                      />
                      {/* <Chip size="small" label={agg} color={statusColor(agg)} /> */}
                    </ListItemButton>
                  );
                })}
            </List>
          </Box>
        </Grid>

        {/* MIDDLE – RULE SETS */}
        {!hideRuleSets && (
          <Grid item xs={3} sx={{ height: "100%", minHeight: 0 }}>
            <Box
              sx={{
                border: "1px solid #D0D0D0",
                borderRadius: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid #D0D0D0",
                  fontWeight: 600,
                  backgroundColor: "#FAFAFA",
                }}
              >
                Rule Sets
              </Box>
              {/* Scrollable List */}
              <Box
                sx={{
                  // flex: 1,
                  overflowY: "auto",
                  minHeight: 0,
                  p: 1,
                  ...componentStyle.scroll,
                }}
              >
                <List dense sx={{ p: 1 }}>
                  {selectedStandard?.rule_sets
                    ?.filter((rs) => rs?.workflows?.length > 0)
                    ?.map((rs) => {
                      const agg = aggregateStatus([rs]);
                      const selected = rs.rule_set_id === selectedRuleSetId;

                      return (
                        <ListItemButton
                          key={rs.rule_set_id}
                          selected={selected}
                          onClick={() => setSelectedRuleSetId(rs.rule_set_id)}
                          sx={{
                            mb: 1,
                            borderRadius: 0,
                            border: selected
                              ? "2px solid #26890D"
                              : "1px solid #D0D0D0",
                            backgroundColor: selected ? "#F3FAF4" : "#FFFFFF",
                            "&.Mui-selected": {
                              backgroundColor: "#F3FAF4",
                            },
                          }}
                        >
                          <ListItemText
                            primary={rs.rule_set_name}
                            // secondary={`${rs.workflows.length} workflows`}
                          />
                          {/* <Chip size="small" label={agg} color={statusColor(agg)} /> */}
                        </ListItemButton>
                      );
                    })}
                </List>
              </Box>
            </Box>
          </Grid>
        )}
        {showAiPanel && (
          <Grid item xs={5} sx={{ height: "100%", minHeight: 0 }}>
            <TicketListDecisionMatrix
              selectedStandard={selectedStandard}
              similarTickets={similarTickets}
              disabledStandards={disabledStandards}
              onCreateTicket={handleCreateTicket}
              onSelectTicket={handelGetAiSuggestion}
              createdTicketIds={[]}
            />
          </Grid>
        )}
        {openAiPanel && (
          <Grid item xs={2} sx={{ height: "100%", minHeight: 0 }}>
            <AiSuggestionDecisionMatrix
              entityDetails={entityDetails}
              setOpen={setOpenAiPanel}
              setCreateTicketData={setCreateTicketData}
              uploads={uploads}
              setUploads={setUploads}
              selectedTicket={selectedTicket}
            />
          </Grid>
        )}
        {/* RIGHT – QUESTIONNAIRE */}
        {/* RIGHT – QUESTIONNAIRE */}
        {!openAiPanel && !hideRuleSets && (
          <Grid item xs={5} sx={{ height: "100%", minHeight: 0 }}>
            <Box
              sx={{
                border: "1px solid #D0D0D0",
                borderRadius: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Decision Matrix – Rule Engine
              </Typography>

              <Divider sx={{ my: 2 }} />

              {selectedRuleSet &&
                selectedRuleSet.workflows?.length === 0 &&
                !openAiPanel && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      height: "100%",
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                      No Decision Matrix Engine Defined
                    </Typography>

                    <Typography variant="body2">
                      This rule set is linked to the selected legal standard,
                      but no decision matrix workflow has been configured for
                      it.
                    </Typography>
                  </Box>
                )}

              {/* ───────────── Idle ───────────── */}
              {selectedRuleSet &&
                selectedRuleSet.workflows?.length > 0 &&
                !openAiPanel && (
                  <>
                    {!executionState && (
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Typography sx={{ mb: 2 }}>
                          Start Decision Matrix for selected rule set
                        </Typography>

                        <CustomButtonV2
                          btnName="Start"
                          size="small"
                          handleOnClick={async () => {
                            // CALL START API
                            const res = await onExecuteWorkflow({
                              mode: "START",
                              payload: {
                                workflowId:
                                  selectedRuleSet.workflows?.[0]?.workflow_id,
                                version:
                                  selectedRuleSet.workflows?.[0]
                                    ?.workflow_version,
                              },
                            });
                            if (!isValidExecutionStart(res)) {
                              return;
                            }
                            setExecutionState(res);
                            setAnswers({});
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}

              {/* ───────────── Action Node ───────────── */}
              {selectedRuleSet && selectedRuleSet.workflows?.length > 0 && (
                <>
                  {/* EXISTING Idle / Action / Complete logic */}
                  {(executionState?.currentNodeType === "Action" ||
                    executionState?.type === "Action") && (
                    <Box
                      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <Typography fontWeight={600} sx={{ mb: 1 }}>
                        {getLabel(executionState)}
                      </Typography>

                      <List dense>
                        {executionState.render?.fields?.map((field) => (
                          <ListItemButton
                            key={field.id}
                            selected={answers.answer === field.id}
                            onClick={() => setAnswers({ answer: field.id })}
                            sx={{
                              mb: 1,
                              borderRadius: 0,
                              border:
                                answers.answer === field.id
                                  ? "2px solid #26890D"
                                  : "1px solid #D0D0D0",
                            }}
                          >
                            <ListItemText primary={field.label} />
                          </ListItemButton>
                        ))}
                      </List>

                      <Box
                        sx={{
                          // mt: "auto",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <CustomButtonV2
                          btnName="Back"
                          disabled={executionHistory.length === 0}
                          handleOnClick={() => {
                            const prev =
                              executionHistory[executionHistory.length - 1];
                            setExecutionHistory((h) => h.slice(0, -1));
                            setExecutionState(prev);
                            setAnswers({});
                          }}
                        />

                        <CustomButtonV2
                          btnName="Next"
                          disabled={!answers.answer}
                          handleOnClick={async () => {
                            const nodeId = getCurrentNodeId(executionState);
                            if (!nodeId) {
                              console.warn("No nodeId in execution state");
                              return;
                            }
                            const baseNodeId = nodeId?.replace("_display", "");
                            const res = await onExecuteWorkflow({
                              mode: "COMPLETE",
                              payload: {
                                executionId: executionState.executionId,
                                nodeId: nodeId,
                                input: {
                                  [baseNodeId + "_answer"]: answers.answer,
                                },
                              },
                            });
                            if (!isValidNodeResponse(res)) {
                              console.warn(
                                "Invalid NODE response. Staying in idle state.",
                              );
                              return;
                            }
                            setExecutionHistory((prev) => [
                              ...prev,
                              executionState,
                            ]);
                            setExecutionState(res);
                            setAnswers({});
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* ───────────── Completion Node ───────────── */}
              {selectedRuleSet && selectedRuleSet.workflows?.length > 0 && (
                <>
                  {/* EXISTING Idle / Action / Complete logic */}
                  {executionState?.type === "Complete" && (
                    <Box sx={{ flex: 1 }}>
                      {(() => {
                        const outcome = executionState.context?.outcome;

                        switch (outcome) {
                          case WORKFLOW_OUTCOMES.CREATE_TICKET:
                            return (
                              <>
                                <Typography fontWeight={700} sx={{ mb: 1 }}>
                                  Action Required
                                </Typography>

                                <Typography sx={{ mb: 2 }}>
                                  Based on your answers, this workflow requires
                                  a ticket to be created.
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                {/* <CustomButtonV2
                                btnName="Create Ticket"
                                handleOnClick={() => {
                                  onClose();
                                  console.log(
                                    "DecisionMatrix sending standard_number:",
                                    selectedStandard?.standard_number,
                                  );
                                  onDecisionMatrixAction?.({
                                    standard_name:
                                      selectedStandard.standard_number,
                                  });
                                }}
                              /> */}
                                <CustomButtonV2
                                  btnName="Finish"
                                  handleOnClick={async () => {
                                    // onClose();

                                    await handelGetSimilerTickets?.();
                                    // setSimilarTickets(tickets);
                                    setShowAiPanel(true);
                                    setHideRuleSets(true);
                                  }}
                                />
                              </>
                            );

                          case WORKFLOW_OUTCOMES.DO_NOT_CREATE:
                            return (
                              <>
                                <Typography fontWeight={700} sx={{ mb: 1 }}>
                                  No Action Required
                                </Typography>

                                <Typography sx={{ mb: 2 }}>
                                  Based on your answers, no further action is
                                  required.
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <CustomButtonV2
                                  btnName="Close"
                                  handleOnClick={handelCloseDialog}
                                />
                              </>
                            );

                          default:
                            return (
                              <Typography color="error">
                                Workflow completed with unknown outcome.
                              </Typography>
                            );
                        }
                      })()}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
